// @ts-strict-ignore
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { MultiSelectProps } from '@usealloy/component-library';
import { MultiSelect, useSnackBar } from '@usealloy/component-library';

import type { Action } from 'flow/store/engine/types';
import { ActionType } from 'flow/store/engine/constants';
import {
  DEFAULT_TAG_COLOR_IN_HEX_FORMAT, MULTISELECT_PROPS,
} from 'flow/editor/components/constants';
import { findNextActionPrecedence } from 'flow/service/actions';
import { generatePublicId } from 'flow/service/publicIds';
import {
  selectOutcomeActions,
  selectWorkflowProps,
  selectWorkflowRoutingActions,
  selectWorkflowType,
  selectIsTagSwapMode,
  selectWorkflowIsSnippet,
} from 'flow/store/redux/selectors';
import {
  setLinkedChildActionCreateActionPayload,
  updateAction,
} from 'flow/store/redux/slice';
import { useCreateOutcome, useCreateTag } from 'flow/api';

import { UserContext } from '~/dashboard/context';
import { getFormattedTagData } from '~/dashboard/views/workflows/store/engine/transformation/actions';
import { createEphemeralOutcome, getOutcomeByName, isOutcomeEphemeral } from '~/dashboard/views/workflows/service/outcomes/outcomes';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import { expandListInServicesWithLists } from '../../../MenuBar/MenuBarModals/AddActionModals/AddServicesModal/helpers';
import {
  ActionTypeSingleValue,
  DisabledOptionWithTooltip,
  LoadingIndicator,
  Option,
  SingleValue,
} from './dropdownComponents';
import {
  getActionTypeOptions,
  getActionValueDropdownPlaceholder,
  getOutcomeOptions,
  getServiceOptions,
  getSnippetActionTypeOptions,
  getTagOptions,
  getWorkflowRoutingOptions,
  isActionTypeCreatable,
  validateCreateOption,
} from './helpers';
import PreviewMatrixModelDropdowns from './PreviewMatrixModelDropdowns';
import type { ActionTypeOption, ActionValueOption } from './types';
import { LIST_SERVICE_KEY_IN_SERVICES } from 'flow/service/services/constants';
import RuleMultiSelect from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/RuleTemplate/RuleMultiSelect';
import { RuleBuilderContext } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/context';
import { createEphemeralTag, getTagByName } from 'flow/service/tags/tags';
import { swapActionObjectThunk } from 'flow/store/redux/thunks';
import { isInSnippetEditor } from 'flow/editor/components/helpers';

interface Props {
  action?: Action;
  isActionTypeOptionSelectDisabled?: boolean;
  isPreviewAction?: boolean;
  onKeyDownHandlers?: MultiSelectProps['onKeyDownHandlers'];
  presetActionTypeOptions?: ActionTypeOption[];
  presetActionValueOptions?: ActionValueOption[];
  skipActionTypeSelection?: boolean;
  autoFocus?: boolean;
}

export default function PreviewActionTextDropdown({
  action,
  isActionTypeOptionSelectDisabled = false,
  skipActionTypeSelection = false,
  isPreviewAction = false,
  onKeyDownHandlers,
  presetActionTypeOptions,
  presetActionValueOptions,
  autoFocus = false,
}: Props) {
  const addSnackBar = useSnackBar();
  const dispatch = useAppDispatch();
  const { featureFlags, breadcrumbs } = useContext(UserContext);
  const {
    allWorkflows,
    lists,
    customLists,
    outcomes,
    services,
    tags,
  } = useAppSelector(selectWorkflowProps);
  const servicesWithLists = expandListInServicesWithLists(services, lists, customLists);
  const outcomeActions = useAppSelector(selectOutcomeActions);
  const workflowRoutingActions = useAppSelector(selectWorkflowRoutingActions);

  const workflowType = useAppSelector(selectWorkflowType);
  const isTagSwapMode = useAppSelector(selectIsTagSwapMode);
  const isSnippet = useAppSelector(selectWorkflowIsSnippet);

  const useCreateTagMutation = useCreateTag();
  const useCreateOutcomeMutation = useCreateOutcome();
  const newlyCreatedElementName = useRef<string>(null);

  const actionTypeMultiSelectRef = useRef(null);
  const actionValueMultiSelectRef = useRef(null);

  const { instanceId } = useContext(RuleBuilderContext);
  const isRuleBuilderScoped = Boolean(instanceId);

  const [
    selectedActionTypeOption, setSelectedActionTypeOption,
  ] = useState<ActionTypeOption>(presetActionTypeOptions?.[0] ?? null);
  const [
    selectedActionValueOption, setSelectedActionValueOption,
  ] = useState<ActionValueOption>(presetActionValueOptions?.[0] ?? null);

  const actionTypeOptions = useMemo(
    () => {
      if (presetActionTypeOptions) { return presetActionTypeOptions; }
      if (isSnippet) { return getSnippetActionTypeOptions(); }

      return getActionTypeOptions({
        outcomeActions,
        workflowType,
        workflowRoutingActions,
      });
    },
    [
      isSnippet,
      presetActionTypeOptions,
      outcomeActions,
      workflowRoutingActions,
      workflowType,
    ],
  );

  const selectedActionType = selectedActionTypeOption?.meta?.type;
  const isCreatable = isActionTypeCreatable(selectedActionType);
  const isCreatingElement = useCreateTagMutation.isLoading || useCreateOutcomeMutation.isLoading;

  const actionValueOptions = useMemo(() => {
    switch (selectedActionType) {
      case ActionType.TAG:
        return getTagOptions(tags);
      case ActionType.SERVICE:
        return getServiceOptions(servicesWithLists);
      case ActionType.OUTCOME:
        return getOutcomeOptions({
          outcomes,
          outcomeActions,
          isAddingNewOutcome: isPreviewAction,
        });
      case ActionType.WORKFLOW:
        return getWorkflowRoutingOptions(allWorkflows, workflowRoutingActions, isPreviewAction);
      default:
        return [];
    }
  }, [
    allWorkflows,
    isPreviewAction,
    outcomeActions,
    outcomes,
    selectedActionType,
    servicesWithLists,
    tags,
    workflowRoutingActions,
  ]);


  const handleOnActionTypeChange = useCallback((option: ActionTypeOption) => {
    setSelectedActionTypeOption(option);
    setSelectedActionValueOption(null);
  }, []);

  const handleOnActionValueChange = useCallback((option: ActionValueOption) => {
    const {
      label: name,
      meta: {
        underlineColor,
      } = {},
      value: internalValue,
    } = option;

    const workflow = allWorkflows
      .find(w => w.application_id === Number(internalValue));
    setSelectedActionValueOption(option);
    switch (selectedActionType) {
      case ActionType.TAG: {
        // find or create the matching tag prop
        const matchingTagProp = getTagByName(tags, name) ?? createEphemeralTag({ name });
        const formattedTagData = getFormattedTagData(matchingTagProp);
        if (isPreviewAction) {
          dispatch(swapActionObjectThunk(internalValue));
        } else {
          dispatch(setLinkedChildActionCreateActionPayload({
            type: ActionType.TAG,
            args: {
              color: underlineColor,
              name: internalValue,
              publicId: generatePublicId('action', ActionType.TAG),
              ...formattedTagData,
            },
          }));
        }
        break;
      }
      case ActionType.SERVICE: {
        const listItem = internalValue === LIST_SERVICE_KEY_IN_SERVICES
          ? lists.find(l => l.list_name === name) || null
          : null;
        let listType = null;
        if (listItem?.is_whitelist) {
          listType = 'whitelist';
        } else if (listItem?.is_blacklist) {
          listType = 'blacklist';
        }
        dispatch(setLinkedChildActionCreateActionPayload({
          type: ActionType.SERVICE,
          args: {
            internalName: internalValue,
            name,
            publicId: generatePublicId('action', ActionType.SERVICE),
            listId: listItem?.customer_list_id || null,
            listType,
          },
        }));
        break;
      }
      case ActionType.OUTCOME: {
        // find or create the matching outcome prop
        const matchingOutcomeProp = getOutcomeByName(outcomes, internalValue)
          ?? createEphemeralOutcome({ name: internalValue });
        /**
         * when building from the island
         * once intermediate states are fully supported both blocks will be consolidated
         */
        if (isPreviewAction) {
          dispatch(swapActionObjectThunk(internalValue));
        } else {
          dispatch(setLinkedChildActionCreateActionPayload({
            type: ActionType.OUTCOME,
            args: {
              name: internalValue,
              color: matchingOutcomeProp.outcome_color,
              icon: matchingOutcomeProp.icon_name,
              isCaseStatus: matchingOutcomeProp.is_case_status,
              publicId: generatePublicId('action', ActionType.OUTCOME),
              precedenceIndex: findNextActionPrecedence(outcomeActions),
              isEphemeral: isOutcomeEphemeral(matchingOutcomeProp),
            },
          }));
        }
        break;
      }
      case ActionType.WORKFLOW:
        /**
         * when building from the island
         * once intermediate states are fully supported both blocks will be consolidated
         */
        if (isPreviewAction) {
          dispatch(updateAction({
            path: `${action.publicId}.workflow`,
            value: {
              name,
              precedenceIndex: findNextActionPrecedence(workflowRoutingActions),
              workflowId: workflow.application_id,
            },
          }));
        } else {
          dispatch(setLinkedChildActionCreateActionPayload({
            type: ActionType.WORKFLOW,
            args: {
              name,
              publicId: generatePublicId('action', ActionType.WORKFLOW),
              precedenceIndex: findNextActionPrecedence(workflowRoutingActions),
              workflowId: workflow.application_id,
            },
          }));
        }

        break;
    }
  }, [
    action,
    allWorkflows,
    dispatch,
    isPreviewAction,
    outcomeActions,
    outcomes,
    selectedActionType,
    tags,
    workflowRoutingActions,
    lists,
  ]);

  const handleCreateOptionLocal = useCallback((option: ActionValueOption) => {
    const elementName = option.label;
    const ephemeralActionValueOption: ActionValueOption = {
      label: elementName,
      value: elementName,
    };
    handleOnActionValueChange(ephemeralActionValueOption);
  }, [handleOnActionValueChange]);

  const handleCreateOptionRemote = useCallback((option: ActionValueOption) => {
    const elementName = option.label;
    switch (selectedActionType) {
      case ActionType.TAG:
        useCreateTagMutation.mutate({
          tagColor: DEFAULT_TAG_COLOR_IN_HEX_FORMAT,
          tagName: elementName,
        });
        break;
      case ActionType.OUTCOME:
        useCreateOutcomeMutation.mutate({
          outcomeName: elementName,
        });
        break;
    }
    newlyCreatedElementName.current = elementName;
  }, [selectedActionType, useCreateOutcomeMutation, useCreateTagMutation]);

  const handleCreateOption = useCallback((option: ActionValueOption) => {
    // Validate create option
    const validationMessage = validateCreateOption(selectedActionType, option);
    if (validationMessage) {
      addSnackBar({
        title: validationMessage,
        type: 'danger',
        duration: 3000,
      });
      return;
    }

    // TODO - BUILD-3998 - remove library FF as dependency
    if (featureFlags.useTyphonWorkflowEndpoints || isInSnippetEditor(breadcrumbs)) {
      handleCreateOptionLocal(option);
    } else {
      handleCreateOptionRemote(option);
    }
  }, [
    handleCreateOptionLocal,
    handleCreateOptionRemote,
    featureFlags.useTyphonWorkflowEndpoints,
    breadcrumbs,
    addSnackBar,
    selectedActionType,
  ]);

  /**
   * This effect runs when either the create tag or create outcome mutation runs
   * and their underlying queries are invalidated, ie. the component is ready to use the tag
   * or outcome that was just created in the server.
   *
   * The `reset` functions are grabbed from the mutation objects to avoid passing the full
   * object in the effect's dependency array, since the mutation object changes a lot throughout
   * its lifetime, and that would cause this effect to run more times than needed.
   */
  const { reset: resetUseCreateTagMutation } = useCreateTagMutation;
  const { reset: resetUseCreateOutcomeMutation } = useCreateOutcomeMutation;

  useEffect(() => {
    if (newlyCreatedElementName.current
      && (useCreateTagMutation.isSuccess
        || useCreateOutcomeMutation.isSuccess)) {
      const correspondingElementOption = actionValueOptions
        .find(o => o.label === newlyCreatedElementName.current);
      handleOnActionValueChange(correspondingElementOption);
      /**
       * mutations are reset so their status goes back to 'idle'. this allows to safely re-run the
       * mutations in any order as many times as needed without unmounting the component.
       */
      resetUseCreateTagMutation();
      resetUseCreateOutcomeMutation();
      newlyCreatedElementName.current = null;
    }
  }, [
    actionValueOptions,
    handleOnActionValueChange,
    resetUseCreateOutcomeMutation,
    resetUseCreateTagMutation,
    useCreateOutcomeMutation.isSuccess,
    useCreateTagMutation.isSuccess,
  ]);

  useEffect(() => {
    if (skipActionTypeSelection && action?.type === ActionType.TAG) {
      const tagActionTypeOption = actionTypeOptions.find(
        option => option.meta?.type === ActionType.TAG,
      );
      if (tagActionTypeOption) {
        setSelectedActionTypeOption(tagActionTypeOption);
        setSelectedActionValueOption({
          label: action.tag.name,
          value: action.tag.name,
          meta: {
            underlineColor: `#${action.tag.color}`,
          },
        });
      }
    }
  }, [skipActionTypeSelection, action, actionTypeOptions]);

  useEffect(() => {
    if (autoFocus && isTagSwapMode.isActive) {
      setTimeout(() => {
        actionValueMultiSelectRef?.current?.focus();
      }, 300);
    }
  }, [autoFocus, selectedActionType, isTagSwapMode]);

  const Select = isRuleBuilderScoped ? RuleMultiSelect : MultiSelect;

  return (
    <>
      {!skipActionTypeSelection && (
        <Select
          {...MULTISELECT_PROPS}
          components={{
            Option: DisabledOptionWithTooltip<ActionTypeOption>,
            SingleValue: ActionTypeSingleValue,
          }}
          isDisabled={isActionTypeOptionSelectDisabled || isCreatingElement}
          isMulti={false}
          onChange={handleOnActionTypeChange}
          options={actionTypeOptions}
          placeholder="tag..."
          ref={actionTypeMultiSelectRef}
          selectedOptions={[selectedActionTypeOption]}
        />
      )}

      {selectedActionType && selectedActionType !== ActionType.MATRIX_MODEL && (
        <Select
          {...MULTISELECT_PROPS}
          data-testid="previewNewAction"
          data-value={selectedActionValueOption?.value || ''}
          closeMenuOnSelect
          components={{
            LoadingIndicator,
            SingleValue,
            Option: Option<ActionValueOption>,
          }}
          // todo BUILD-637 - refactor MULTISELECT_PROPS to make it easier to compose new styles
          isCreatable={isCreatable}
          // @ts-expect-error - TODO: fix this
          isLoading={isCreatingElement as unknown as boolean}
          isMulti={false}
          onChange={handleOnActionValueChange}
          onCreateOption={isCreatable && handleCreateOption}
          openMenuOnFocus
          options={actionValueOptions}
          placeholder={(
            isCreatingElement
              ? newlyCreatedElementName.current
              : getActionValueDropdownPlaceholder(selectedActionType)
          )}
          ref={actionValueMultiSelectRef}
          value={selectedActionValueOption}
          {...onKeyDownHandlers ? { onKeyDownHandlers } : {}}
        />
      )}
      {
        selectedActionType && selectedActionType === ActionType.MATRIX_MODEL && (
          <PreviewMatrixModelDropdowns />
        )
      }
    </>
  );
}
